import React, { useEffect, useRef } from 'react';
import type { JSONEditorMode, JSONEditorOptions } from 'jsoneditor/dist/jsoneditor-minimalist';
import JSONEditor from 'jsoneditor/dist/jsoneditor-minimalist';

import 'jsoneditor/dist/jsoneditor.css';

export type JsonEditorMode = JSONEditorMode;

export interface JsonEditorProps
  extends Pick<
    JSONEditorOptions,
    'history' | 'search' | 'navigationBar' | 'statusBar' | 'sortObjectKeys' | 'onValidationError'
  > {
  /**
   * The initial document, applied once when the editor mounts; the editor owns the document from
   * then on (React's uncontrolled-input semantics). Callers feed the edited value straight back in,
   * so re-applying it on change would reset the caret on every keystroke.
   */
  defaultValue?: unknown;
  /**
   * Called with the parsed document after each user edit, or with `null` once the text is emptied.
   * Not called while the text is not valid JSON — surface that through `onValidationError`.
   */
  onChange?: (value: unknown) => void;
  /** Initial mode. See the component doc for why a later change is not applied. */
  mode?: JsonEditorMode;
  /** Modes the user may switch between from the editor's own menu. Initial-only, like `mode`. */
  allowedModes?: JsonEditorMode[];
  /** Props for the host `<div>` the editor renders into (typically `style` for its size). */
  htmlElementProps?: React.HTMLAttributes<HTMLDivElement>;
}

/**
 * Thin React host for `jsoneditor` (the minimalist bundle: no Ace, so `mode: 'code'` renders as
 * `text`; no Ajv, so `schema` validation is structural only).
 *
 * Contract: every prop except the callbacks is read once, when the editor is created on mount, and
 * a later change is not applied. Callbacks are read through a ref, so the editor always invokes the
 * latest ones. Every current caller passes the initial-only props as constants, so nothing is lost
 * today; the contract is stated here so a future caller does not expect a controlled component.
 *
 * Why initial-only: jsoneditor has no incremental update path. Its own `setMode` tears the whole
 * editor down and rebuilds it (`JSONEditor.js`, `setMode`), carrying only the document text across —
 * undo history, caret and scroll position are lost — and it refuses to run when the mode is
 * unchanged, so it cannot re-apply the other options either. Honouring those props would therefore
 * mean destroying and re-creating the editor from here, on top of a document the user may be
 * typing in. Not worth building for a caller that does not exist.
 *
 * If one does: `mode` can follow the prop via `editor.setMode(mode)` in an effect guarded by
 * `editor.getMode() !== mode` (so a re-render does not undo a switch the user made from the menu);
 * the remaining options need the `getText()` → `destroy()` → construct → `setText()` sequence
 * `setMode` uses internally, keyed on the options' content rather than identity, because callers
 * pass `allowedModes` as an inline literal.
 */
const JsonEditor: React.FC<JsonEditorProps> = ({
  defaultValue,
  onChange,
  mode = 'tree',
  allowedModes,
  history = false,
  search = true,
  navigationBar = true,
  statusBar = true,
  sortObjectKeys = false,
  onValidationError,
  htmlElementProps,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const latest = useRef({ defaultValue, onChange, onValidationError });
  latest.current = { defaultValue, onChange, onValidationError };

  // Mount-only on purpose: every option here is read once by the editor's constructor, and
  // re-creating the editor would discard whatever the user is typing.
  useEffect(() => {
    if (!containerRef.current) {
      return undefined;
    }

    const editor = new JSONEditor(containerRef.current, {
      mode,
      modes: allowedModes,
      history,
      search,
      navigationBar,
      statusBar,
      sortObjectKeys,
      onValidationError: (errors) => latest.current.onValidationError?.(errors),
      onChange: () => {
        const { onChange: handleChange, defaultValue: currentValue } = latest.current;
        if (!handleChange) {
          return;
        }
        try {
          if (editor.getText() === '') {
            handleChange(null);
            return;
          }
          const json = editor.get();
          if (json !== currentValue) {
            handleChange(json);
          }
        } catch {
          // Not valid JSON yet — the editor reports this through `onValidationError`.
        }
      },
    });
    editor.set(latest.current.defaultValue);

    return () => editor.destroy();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div {...htmlElementProps} ref={containerRef} />;
};

export default JsonEditor;
